import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Workgroup } from "../types";
import { Select } from "./ui/select";
import { cn } from "../lib/utils";
import { fetchWorkgroupDetails } from "./firestoreFunctions";
import { getUserId } from "../firebase";

interface WorkgroupSelectProps {
  selectedWorkgroup?: string | null;
  onSelect: (workgroupId: string) => void;
  disabled?: boolean;
  label?: string | null;
  placeholder?: string;
  emptyMessage?: string;
  containerClassName?: string;
  selectClassName?: string;
  includeEmptyOption?: boolean;
  dataTestId?: string;
  isLoading?: boolean;
  loadingMessage?: string;
  autoSelectFirst?: boolean;
  onWorkgroupsLoaded?: (workgroups: Workgroup[]) => void;
  onLoadingChange?: (loading: boolean) => void;
  workgroups?: Workgroup[];
}

export function WorkgroupSelect({
  workgroups,
  selectedWorkgroup,
  onSelect,
  disabled = false,
  label,
  placeholder = "Select a workgroup",
  emptyMessage = "No workgroups available",
  containerClassName,
  selectClassName,
  includeEmptyOption = false,
  dataTestId = "workgroup-select",
  isLoading = false,
  loadingMessage = "Loading workgroups…",
  autoSelectFirst = true,
  onWorkgroupsLoaded,
  onLoadingChange,
}: WorkgroupSelectProps) {
  const selectId = React.useId();
  const resolvedLabel = label === undefined ? "Workgroup" : label;
  const showLabel = resolvedLabel !== null && resolvedLabel !== "";
  const [internalWorkgroups, setInternalWorkgroups] = useState<Workgroup[]>([]);
  const [internalLoading, setInternalLoading] = useState<boolean>(
    () => !workgroups && !isLoading
  );
  const [fetchError, setFetchError] = useState<string | null>(null);

  const effectiveWorkgroups = workgroups ?? internalWorkgroups;
  const loading =
    (workgroups ? false : internalLoading) || Boolean(isLoading);
  const hasWorkgroups = effectiveWorkgroups.length > 0;
  const value = selectedWorkgroup ?? "";
  const disableSelect = disabled || loading || (!hasWorkgroups && !includeEmptyOption);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  useEffect(() => {
    let isMounted = true;

    const populateFromProp = (items: Workgroup[]) => {
      if (!isMounted) return;
      setInternalWorkgroups(items);
      setInternalLoading(false);
      setFetchError(null);
      onWorkgroupsLoaded?.(items);
      onLoadingChange?.(false);
    };

    if (workgroups) {
      populateFromProp(workgroups);
      return () => {
        isMounted = false;
      };
    }

    const loadWorkgroups = async () => {
      if (!isMounted) {
        return;
      }

      setInternalLoading(true);
      onLoadingChange?.(true);

      try {
        const userId = await getUserId();
        if (!userId) {
          if (!isMounted) return;
          setInternalWorkgroups([]);
          setFetchError("User not authenticated");
          onWorkgroupsLoaded?.([]);
          return;
        }

        const results = await fetchWorkgroupDetails(userId);

        if (!isMounted) return;
        setInternalWorkgroups(results);
        setFetchError(null);
        onWorkgroupsLoaded?.(results);
      } catch (error) {
        if (!isMounted) return;
        console.error("[WorkgroupSelect] Failed to load workgroups", error);
        setInternalWorkgroups([]);
        setFetchError("Failed to load workgroups");
        onWorkgroupsLoaded?.([]);
      } finally {
        if (!isMounted) return;
        setInternalLoading(false);
        onLoadingChange?.(false);
      }
    };

    loadWorkgroups();

    return () => {
      isMounted = false;
    };
  }, [workgroups, onWorkgroupsLoaded, onLoadingChange]);

  useEffect(() => {
    if (loading) return;
    if (!effectiveWorkgroups.length) return;
    const selectedValue = selectedWorkgroup ?? "";
    const exists = effectiveWorkgroups.some((wg) => wg.id === selectedValue);
    if (!exists && autoSelectFirst && !includeEmptyOption) {
      onSelect(effectiveWorkgroups[0].id);
    }
  }, [
    loading,
    effectiveWorkgroups,
    selectedWorkgroup,
    autoSelectFirst,
    includeEmptyOption,
    onSelect,
  ]);

  return (
    <div className={cn("space-y-1.5", containerClassName)}>
      {showLabel && (
        <label
          htmlFor={selectId}
          className="text-xs font-medium text-muted-foreground px-1"
        >
          {resolvedLabel}
        </label>
      )}
      <Select
        id={selectId}
        value={value}
        onChange={(event) => onSelect(event.target.value)}
        disabled={disableSelect}
        className={cn(showLabel ? "mt-1" : undefined, selectClassName)}
        data-testid={dataTestId}
        aria-label={!showLabel ? resolvedLabel ?? "Workgroup" : undefined}
        aria-busy={loading}
      >
        {loading ? (
          <option value="">{loadingMessage}</option>
        ) : !hasWorkgroups ? (
          <option value="">{emptyMessage}</option>
        ) : (
          <>
            {(includeEmptyOption || value === "") && (
              <option value="">{placeholder}</option>
            )}
            {effectiveWorkgroups.map((workgroup) => (
              <option key={workgroup.id} value={workgroup.id}>
                {workgroup.name || workgroup.id}
              </option>
            ))}
          </>
        )}
      </Select>
      {loading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground px-1">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          <span>{loadingMessage}</span>
        </div>
      )}
      {fetchError && !loading && (
        <p className="text-xs text-destructive px-1">{fetchError}</p>
      )}
    </div>
  );
}
